/**
 * File groups (docs/user-flow.md §6): read a client's groups, and the
 * mutations that create, rename, delete and set the membership of one.
 */
import {
	useMutation,
	useQueries,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';
import * as fileGroupsApi from '../api/FileGroups';
import { parsed } from '../api/parse';
import type { ModuleCode } from '../features/modules/modules';
import {
	fileGroupDetailSchema,
	fileGroupRecordSchema,
	type FileGroupDetail,
} from '../schemas';
import { keys } from './keys';

export function useFileGroupsQuery(
	clientId: string | null,
	module: ModuleCode,
	enabled = true,
) {
	return useQuery({
		queryKey: keys.fileGroups.forClient(clientId ?? '', module),
		queryFn: async () => {
			const path = `/clients/${clientId}/file-groups?module=${module}`;
			return parsed(
				z.array(fileGroupRecordSchema),
				await fileGroupsApi.getFileGroupsForClient(
					clientId as string,
					module,
				),
				path,
			);
		},
		enabled: Boolean(clientId) && enabled,
	});
}

async function readFileGroup(groupId: string): Promise<FileGroupDetail> {
	return parsed(
		fileGroupDetailSchema,
		await fileGroupsApi.getFileGroup(groupId),
		`/file-groups/${groupId}`,
	);
}

export function useFileGroupQuery(groupId: string | null, enabled = true) {
	return useQuery({
		queryKey: keys.fileGroups.detail(groupId ?? ''),
		queryFn: () => readFileGroup(groupId as string),
		enabled: Boolean(groupId) && enabled,
	});
}

/** Every group's detail, sharing each group's own cache entry. */
export function useFileGroupDetails(groupIds: string[]) {
	return useQueries({
		queries: groupIds.map((groupId) => ({
			queryKey: keys.fileGroups.detail(groupId),
			queryFn: () => readFileGroup(groupId),
		})),
		combine: (results) => ({
			details: results.flatMap((result) =>
				result.data ? [result.data] : [],
			),
			ready: results.every((result) => result.isSuccess),
			error: results.find((result) => result.error)?.error ?? null,
		}),
	});
}

/**
 * Saves a group's membership as `change(current)`, reading `current` fresh
 * first: the PUT replaces the whole set, so working from a stale copy would
 * undo whatever someone else saved since this screen loaded.
 */
export async function changeGroupMembers(
	groupId: string,
	change: (current: string[]) => string[],
) {
	const fresh = await readFileGroup(groupId);
	return fileGroupsApi.setFileGroupFiles(
		groupId,
		change(fresh.files.map((file) => file.pkFileId)),
	);
}

export function useChangeGroupMembers(clientId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			groupId,
			change,
		}: {
			groupId: string;
			change: (current: string[]) => string[];
		}) => changeGroupMembers(groupId, change),
		onSettled: (_data, _error, { groupId }) => {
			void queryClient.invalidateQueries({
				queryKey: keys.fileGroups.forClientAnyModule(clientId),
			});
			void queryClient.invalidateQueries({
				queryKey: keys.fileGroups.detail(groupId),
			});
		},
	});
}

export function useCreateFileGroup(clientId: string, module: ModuleCode) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (name: string) =>
			fileGroupsApi.createFileGroup(clientId, name, module),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: keys.fileGroups.forClientAnyModule(clientId),
			}),
	});
}

export function useRenameFileGroup(clientId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ groupId, name }: { groupId: string; name: string }) =>
			fileGroupsApi.renameFileGroup(groupId, name),
		onSuccess: (_data, { groupId }) => {
			void queryClient.invalidateQueries({
				queryKey: keys.fileGroups.forClientAnyModule(clientId),
			});
			void queryClient.invalidateQueries({
				queryKey: keys.fileGroups.detail(groupId),
			});
		},
	});
}

export function useDeleteFileGroup(clientId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (groupId: string) => fileGroupsApi.deleteFileGroup(groupId),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: keys.fileGroups.forClientAnyModule(clientId),
			}),
	});
}

/** Saves a group's whole membership as given — used by the old File groups
 *  screen (FileGroupsScreen, kept but no longer routed). Client Documents uses
 *  `useChangeGroupMembers`, which re-reads the group first. */
export function useSetFileGroupFiles(clientId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			groupId,
			fileIds,
		}: {
			groupId: string;
			fileIds: string[];
		}) => fileGroupsApi.setFileGroupFiles(groupId, fileIds),
		onSuccess: (_data, { groupId }) => {
			void queryClient.invalidateQueries({
				queryKey: keys.fileGroups.forClientAnyModule(clientId),
			});
			void queryClient.invalidateQueries({
				queryKey: keys.fileGroups.detail(groupId),
			});
		},
	});
}
